<template>
    <div>
        <slot :get-campaigns="getCampaigns" />
        <v-data-table-server
            :headers="headers"
            :hide-default-header="isMobile"
            :items="campaigns"
            :items-per-page-options="[10, 15, 30, -1]"
            :loading="isCampaignsLoading"
            :options="tableOptions"
            :mobile-breakpoint="0"
            :items-length="campaignsTotalCount"
            :hide-default-footer="!campaignsTotalCount && campaigns.length <= tableOptions.itemsPerPage"
            disable-sort
            class="elevation-1 table-server"
            @update:options="onChangeTableOptions"
        >
            <template v-slot:item.name="{item}">
                <nuxt-link
                    no-prefetch
                    :to="{name: 'campaign-details', params: {id: item.campaignId ? item.campaignId : 0}}"
                    :data-cy="`link-campaign-${item.campaignId}`"
                >
                    {{ item.name }}
                </nuxt-link>
            </template>
            <template v-slot:item.type="{item}">
                {{ item.type ? $t(`campaign_types.${item.type}`) : '' }}
            </template>
            <template v-slot:item.budget="{item}">
                <template v-if="item.budgetValue && item.budgetCurrency">
                    {{ $filters.priceFormat(item.budgetValue, item.budgetCurrency) }}
                </template>
            </template>
            <template v-slot:item.dateRange="{item}">
                {{ $filters.dateShortFormat(item.dateFrom) }} - {{ $filters.dateShortFormat(item.dateTo) }}
            </template>
            <template v-slot:item.assignedUser="{item}">
                {{ item.assignedUser ? userName(item.assignedUser) : '' }}
            </template>
            <template v-slot:item.status="{item}">
                <v-switch
                    :model-value="item.status"
                    :label="item.status ? $t('active_female') : $t('inactive_female')"
                    color="primary"
                    hide-details
                    density="compact"
                    @update:model-value="changeActive(item)"
                />
            </template>
        </v-data-table-server>
    </div>
</template>

<script lang="ts">
    import {Component, Emit, Prop, Vue} from 'vue-facing-decorator'
    import type {AssignedUserType, CampaignType} from '~/utils/types/account/campaigns/types'

    type TableOptions = {
        itemsPerPage: number
        page: number
    }

    @Component({
        components: {},
    })
    export class Campaigns extends Vue {
        @Prop({default: []}) campaigns: CampaignType[]
        @Prop({default: true, type: Boolean}) isCampaignsLoading: boolean
        @Prop({default: 0, type: Number}) campaignsTotalCount: number

        tableOptions: TableOptions = {
            itemsPerPage: 10,
            page: 1,
        }

        @Emit()
        getCampaigns(): TableOptions {
            return this.tableOptions
        }

        onChangeTableOptions(options): void {
            this.tableOptions = options
            this.getCampaigns()
        }

        async changeActive(item): Promise<void> {
            item.status = !item.status

            try {
                await this.$api.campaigns.patch(item.campaignId, {status: item.status})
                this.$toast.success(this.$t('messages.success.campaign_saved'))
            } catch (e) {
                this.$toast.error(this.$t('data_update_fail'))
            } finally {
                this.getCampaigns()
            }
        }

        userName({firstName, lastName, id}: AssignedUserType): string {
            return id ? `${firstName || ''} ${lastName || ''}` : ''
        }

        get headers() {
            return [
                {title: `${this.$t('name')}`, value: 'name'},
                {title: this.$t('type'), value: 'type', width: '10%'},
                {title: this.$t('budget'), value: 'budget', align: 'end'},
                {title: this.$t('dates'), value: 'dateRange'},
                {title: this.$t('assigned_to'), value: 'assignedUser'},
                {title: this.$t('status'), value: 'status', width: '10%', minWidth: '140px'},
            ]
        }

        get isMobile(): boolean {
            return this.$breakpoint.smAndDown
        }
    }

    export default Campaigns
</script>

<style scoped lang="scss">
    @use 'vuetify/settings';
    @use 'sass:map';
    :deep(div) {
        th:nth-of-type(3) .v-data-table-header__content {
            margin-right: 150px;
        }
        td:nth-of-type(3) p {
            margin-right: 150px;
        }
        td:nth-of-type(4) {
            white-space: nowrap;
        }
    }

    @media #{map.get(settings.$display-breakpoints, 'md-and-down')} {
        :deep(div) {
            th:nth-of-type(3) .v-data-table-header__content {
                margin-right: 0;
            }
            td:nth-of-type(3) p {
                margin-right: 0;
            }
        }
    }
</style>
